import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CalendarBlank } from "components/icons/CalendarBlank";
import { Document } from "components/icons/Document";
import { Info } from "components/icons/Info";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import { Warning } from "components/icons/Warning";
import Wind from "components/icons/Wind";
import { DISPLAY_DATE } from "constants/date";
import { DASHBOARD_STATUS } from "constants/task";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAddProductsToCartAndCheckoutMutation } from "store/apis/billing";
import { useGetAllTasksByEntityQuery } from "store/apis/filing";
import { useGetBaseTaskTemplatesQuery } from "store/apis/getTemplates";
import {
  StateRegistrationNameType,
  StateRegistrationsRes,
} from "store/apis/StateRegistrations";
import { BillingInvoice } from "types/Models/billing";
import { EntityDataAttribute } from "types/Models/entity";
import {
  STATE_ANNUAL_REPORTS,
  STATE_FRANCHISE_TAX_DELAWARE,
} from "types/Models/services";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { isStateComplianceDeadlineTask } from "util/isStateComplianceDeadlineTask";
import { COMPLIANCE_SCORE_DONE_STATUSES, isFqDateAttr } from "./constants";
import { CollapsibleCard } from "./CollapsibleCard";
import { DocumentsCard } from "./DocumentsCard";
import { EntityDetailsCard } from "./EntityDetailsCard";
import { RegistrationCards } from "./RegistrationCards";
import { StateCredentialsCard } from "./StateCredentialsCard";
import { TickMarkBlue } from "components/icons/TickMarkBlue";
import { DeadlineItem, DeadlineItemSkeleton } from "./DeadlineItem";
import { MissingInfoModal } from "./MissingInfoModal";

// ─── REGISTRATION_TYPE_LABELS ──────────────────────────────────────────────────

export const REGISTRATION_TYPE_LABELS: Record<string, string> = {
  SECRETARY_OF_STATE: "Secretary of state",
  REGISTERED_AGENT: "Registered agent",
  PAY_ROLL: "Payroll",
};

// ─── Main ──────────────────────────────────────────────────────────────────────

type Props = {
  stateDetail: StateRegistrationsRes;
  entityAttributes: EntityDataAttribute[];
  entityId: string;
  entityName: string;
  stateId: string;
  missingFields: EntityDataAttribute[];
  defaultOpenMissingInfo?: boolean;
  onRegisterNow: (type: StateRegistrationNameType) => void;
  isRenewLoading?: boolean;
};

export const StateComplianceDashboard = ({
  stateDetail,
  entityAttributes,
  entityId,
  entityName,
  stateId,
  missingFields,
  defaultOpenMissingInfo = false,
  onRegisterNow,
  isRenewLoading = false,
}: Props) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { isForeignCA, isAdmin } = useRoleBasedView();
  const history = useHistory();
  const { alertToast } = useToast();

  const missingInfoModal = useModal(defaultOpenMissingInfo);
  const [invoice, setInvoice] = useState<BillingInvoice | null>(null);
  const checkoutModal = useModal();
  const [checkingOutTaskId, setCheckingOutTaskId] = useState<string | null>(
    null
  );

  const [checkout] = useAddProductsToCartAndCheckoutMutation();

  const currentYear = dayjs().year();
  const prevYear = (currentYear - 1).toString();
  const nextYear = currentYear.toString();

  const { data: baseTemplatesData, isLoading: isLoadingBaseTemplates } =
    useGetBaseTaskTemplatesQuery({});
  const allTemplates = Object.values(baseTemplatesData ?? {}).flat();

  const annualReportTemplateId = allTemplates.find(
    (t) => t.base_task_key === STATE_ANNUAL_REPORTS
  )?.uuid;

  const franchiseTaxDelawareTemplateId = allTemplates.find(
    (t) => t.base_task_key === STATE_FRANCHISE_TAX_DELAWARE
  )?.uuid;

  const relevantBaseTaskTemplateIds = [
    annualReportTemplateId,
    franchiseTaxDelawareTemplateId,
  ].filter((id): id is string => Boolean(id));

  const { data: prevYearData, isFetching: isFetchingPrevYearTasks } =
    useGetAllTasksByEntityQuery(
      { groupId: groupId!, entity_id: entityId, filing_season: prevYear },
      { skip: !groupId || !entityId }
    );

  const { data: currentYearData, isFetching: isFetchingCurrentYearTasks } =
    useGetAllTasksByEntityQuery(
      { groupId: groupId!, entity_id: entityId, filing_season: nextYear },
      { skip: !groupId || !entityId }
    );

  const isLoadingStateComplianceDeadlines =
    isLoadingBaseTemplates ||
    isFetchingPrevYearTasks ||
    isFetchingCurrentYearTasks;

  // is_inkle_ra on the REGISTERED_AGENT sub-object is the authoritative flag set by BE.
  // inkle_ra_active at the top level is the same value; expiration date may be null for legacy subs.
  const inkleRaIsActive = Boolean(stateDetail.REGISTERED_AGENT.is_inkle_ra);
  const inkleRaExpirationDate = stateDetail.inkle_ra_expiration_date;
  const externalRaExpirationDate = stateDetail.external_ra_expiration_date;

  const inkleRaExpiryDaysLeft = inkleRaExpirationDate
    ? dayjs(inkleRaExpirationDate).diff(dayjs(), "day")
    : null;

  const externalRaExpiryDaysLeft = externalRaExpirationDate
    ? dayjs(externalRaExpirationDate).diff(dayjs(), "day")
    : null;

  const inkleRaIsExpiringSoon =
    inkleRaExpiryDaysLeft !== null &&
    inkleRaExpiryDaysLeft <= 30 &&
    inkleRaExpiryDaysLeft >= 0;

  const externalRaIsExpiringSoon =
    externalRaExpiryDaysLeft !== null &&
    externalRaExpiryDaysLeft <= 30 &&
    externalRaExpiryDaysLeft >= 0;

  const fqDateAttr = !stateDetail.is_home_state
    ? stateDetail.SECRETARY_OF_STATE.data_attributes.find((a) =>
        isFqDateAttr(a)
      )
    : null;
  const fqDate = (() => {
    if (stateDetail.is_home_state || !fqDateAttr?.value) return null;
    const parsed = dayjs(String(fqDateAttr.value));
    return parsed.isValid() ? String(fqDateAttr.value) : null;
  })();

  const incorporationDateAttr = stateDetail.is_home_state
    ? entityAttributes.find((a) => a.name === "date_of_incorporation")
    : null;
  const incorporationDate = (() => {
    if (!stateDetail.is_home_state || !incorporationDateAttr?.value)
      return null;
    const parsed = dayjs(String(incorporationDateAttr.value));
    return parsed.isValid() ? String(incorporationDateAttr.value) : null;
  })();

  const anniversaryDaysLeft = (() => {
    const baseDate = fqDate ?? incorporationDate;
    if (!baseDate) return null;
    const today = dayjs().startOf("day");
    let nextAnniversary = dayjs(baseDate).year(today.year()).startOf("day");
    if (nextAnniversary.isBefore(today, "day")) {
      nextAnniversary = nextAnniversary.add(1, "year");
    }
    return nextAnniversary.diff(today, "day");
  })();

  const anniversaryIsNear =
    anniversaryDaysLeft !== null &&
    anniversaryDaysLeft <= 30 &&
    anniversaryDaysLeft >= 0;

  const showInkleRaExpiryBanner = inkleRaIsExpiringSoon;

  // Combined flag for RegistrationCards warning styling
  const raIsExpiringSoon = inkleRaIsExpiringSoon || externalRaIsExpiringSoon;
  const raExpirationDate =
    inkleRaExpirationDate ?? externalRaExpirationDate ?? undefined;

  const allTasks = [
    ...(prevYearData?.tasks ?? []),
    ...(currentYearData?.tasks ?? []),
  ].filter((t) =>
    isStateComplianceDeadlineTask(t, relevantBaseTaskTemplateIds)
  );

  const stateTasksAll = allTasks.filter((t) =>
    t.task_title.includes(stateDetail.state)
  );

  const SIX_MONTHS_FROM_NOW = dayjs().add(6, "month");
  const NINETY_DAYS_AGO = dayjs().subtract(90, "day").startOf("day");

  const isFinal = (t: (typeof stateTasksAll)[number]) =>
    "is_final" in t.current_state && t.current_state.is_final;

  const byDeadlineAsc = (
    a: (typeof stateTasksAll)[number],
    b: (typeof stateTasksAll)[number]
  ) => dayjs(a.deadline_date!).valueOf() - dayjs(b.deadline_date!).valueOf();

  const upcomingStateTasks = stateTasksAll
    .filter(
      (t) =>
        t.deadline_date &&
        !t.is_archived &&
        !isFinal(t) &&
        !dayjs(t.deadline_date).isBefore(NINETY_DAYS_AGO) &&
        dayjs(t.deadline_date).isBefore(SIX_MONTHS_FROM_NOW)
    )
    .sort(byDeadlineAsc);

  const completedStateTasks = stateTasksAll
    .filter((t) => !t.is_archived && isFinal(t))
    .sort(byDeadlineAsc);

  const futureStateTasks = stateTasksAll
    .filter(
      (t) =>
        t.deadline_date &&
        !t.is_archived &&
        dayjs(t.deadline_date).isAfter(SIX_MONTHS_FROM_NOW) &&
        !isFinal(t)
    )
    .sort(byDeadlineAsc);

  // A launched task can still be missing its deadline (e.g. ETD not filled in
  // yet) — it's a real, active filing and shouldn't disappear from the card
  // just because there's no date to sort it by.
  const noDeadlineStateTasks = stateTasksAll.filter(
    (t) => !t.deadline_date && !t.is_archived && !isFinal(t)
  );

  const calendarTasks =
    upcomingStateTasks.length > 0
      ? [...upcomingStateTasks, ...noDeadlineStateTasks]
      : completedStateTasks.length > 0
      ? completedStateTasks
      : [...futureStateTasks, ...noDeadlineStateTasks];

  const filingsTemplateParam = [
    annualReportTemplateId,
    franchiseTaxDelawareTemplateId,
  ]
    .filter(Boolean)
    .join(",");

  const filingsUrl = isAdmin
    ? `/admin/crm/${groupId}/new-filings?FILINGS=${filingsTemplateParam}`
    : `/tax/filings?FILINGS=${filingsTemplateParam}`;

  const goToFilings = () => history.push(filingsUrl);

  const handleStartFiling = async (task: TaskDeadlineOverviewItem) => {
    if (!groupId) return;
    setCheckingOutTaskId(task.base_task_template.uuid);
    try {
      const result = await checkout({
        groupId,
        entityId,
        productData: [
          {
            entity_id: entityId,
            product_id: task.base_task_template.uuid,
            product_content_type_id: task.product_content_type_id!,
            season: String(task.season),
            quantity: 1,
            payment_amount: Number(task.payment_amount) || 0,
            alarm_id: task.alarm_uuid || undefined,
          },
        ],
        from_alarms: true,
      }).unwrap();
      setInvoice(result.invoice);
      checkoutModal.open();
    } catch (e) {
      alertToast({ message: (e as BackendError)?.data?.error?.message });
    } finally {
      setCheckingOutTaskId(null);
    }
  };

  const closeCheckoutModal = () => {
    setInvoice(null);
    checkoutModal.close();
  };

  const onPaymentSuccess = () => {
    closeCheckoutModal();
    history.push(filingsUrl);
  };

  const upcomingDeadlines = calendarTasks.slice(0, 5).map((task) => {
    const daysLeft = task.deadline_date
      ? dayjs(task.deadline_date).diff(dayjs(), "day")
      : null;
    const isTaskFinal =
      "is_final" in task.current_state && task.current_state.is_final;
    const isOverdue = daysLeft !== null && daysLeft < 0 && !isTaskFinal;
    const isOngoing =
      task.dashboard_status === DASHBOARD_STATUS.ONGOING && !isTaskFinal;

    let effectiveDashboardStatus: string;
    if (isTaskFinal) {
      effectiveDashboardStatus = "COMPLETED";
    } else if (isOverdue) {
      effectiveDashboardStatus = "OVERDUE";
    } else {
      effectiveDashboardStatus = task.dashboard_status;
    }

    let actionLabel: string | undefined;
    let onAction: (() => void) | undefined;

    if (isTaskFinal) {
      actionLabel = "View";
      onAction = task.task_uuid
        ? () => history.push(`/tax/filings/${task.task_uuid}`)
        : goToFilings;
    } else if (task.task_uuid || isOngoing) {
      actionLabel = "View filing";
      onAction = task.task_uuid
        ? () => history.push(`/tax/filings/${task.task_uuid}`)
        : goToFilings;
    } else {
      actionLabel = "Start filing";
      onAction = () => handleStartFiling(task);
    }

    return {
      task,
      daysLeft,
      isUrgent:
        !isTaskFinal && daysLeft !== null && (daysLeft < 0 || daysLeft <= 14),
      isWarn:
        !isTaskFinal && daysLeft !== null && daysLeft <= 30 && daysLeft > 14,
      effectiveDashboardStatus,
      actionLabel,
      onAction,
      isLoading:
        !task.task_uuid &&
        !isOngoing &&
        checkingOutTaskId === task.base_task_template.uuid,
    };
  });

  const showSosRequiredBanner = stateDetail.sos_required_for_payroll;
  const hasExternalRaDetails = Boolean(externalRaExpirationDate);

  const showRenewWithInkleBanner =
    !inkleRaIsActive && anniversaryIsNear && hasExternalRaDetails;
  const showInkleRaBanner =
    !inkleRaIsActive && !anniversaryIsNear && hasExternalRaDetails;
  const showInkleRaActiveBanner = inkleRaIsActive;

  const registrationsArePending = [
    stateDetail.SECRETARY_OF_STATE.status,
    stateDetail.PAY_ROLL.status,
  ].some(
    (status) =>
      !(COMPLIANCE_SCORE_DONE_STATUSES as readonly string[]).includes(status)
  );

  return (
    <div className="t-flex t-flex-col t-gap-4">
      {showInkleRaExpiryBanner && inkleRaExpirationDate && (
        <Banner
          type="warning"
          icon={
            <span className="t-text-orange t-h-full t-flex t-items-center">
              <Warning color="currentColor" size="16" />
            </span>
          }
          description={`Your Inkle Registered Agent in ${
            stateDetail.state
          } expires on ${dayjs(inkleRaExpirationDate).format(
            DISPLAY_DATE
          )}. Renew to stay compliant.`}
          cta={
            <Button
              size="small"
              customType="link"
              onClick={() => onRegisterNow("REGISTERED_AGENT")}
              isLoading={isRenewLoading}
              disabled={isRenewLoading}
            >
              Renew with Inkle
            </Button>
          }
        />
      )}

      {showSosRequiredBanner && (
        <Banner
          type="warning"
          icon={
            <span className="t-text-orange t-h-full t-flex t-items-center">
              <Warning color="currentColor" size="16" />
            </span>
          }
          description={`Payroll registration requires a Secretary of State registration first. Please complete the SOS registration for ${stateDetail.state} before proceeding with payroll.`}
          cta={
            <Button
              size="small"
              customType="link"
              onClick={() => onRegisterNow("SECRETARY_OF_STATE")}
            >
              Complete SOS registration
            </Button>
          }
        />
      )}

      <div className="t-flex t-gap-4 t-items-start sm:t-flex-col sm:t-items-stretch">
        <div className="t-flex-1 t-flex t-flex-col t-gap-4 t-min-w-0">
          <CollapsibleCard
            title="Registrations"
            icon={<Document width={16} height={16} />}
            tag={
              registrationsArePending && (
                <Tag tagType="orange" size="small" icon={false} rounded>
                  Pending
                </Tag>
              )
            }
          >
            <RegistrationCards
              stateDetail={stateDetail}
              raIsExpiringSoon={raIsExpiringSoon}
              raExpirationDate={raExpirationDate}
              onRegisterNow={onRegisterNow}
              entityId={entityId}
              onViewFiling={goToFilings}
            />
          </CollapsibleCard>

          <CollapsibleCard
            title="State Compliance"
            icon={<CalendarBlank width={16} height={16} />}
          >
            {isLoadingStateComplianceDeadlines ? (
              <DeadlineItemSkeleton />
            ) : upcomingDeadlines.length === 0 ? (
              <div className="t-flex t-flex-col t-items-center t-justify-center t-py-10 t-gap-2">
                <span className="t-text-neutral-40">
                  <Wind />
                </span>
                <div className="t-text-body-sm t-text-neutral-40">
                  There are no upcoming or pending filings for this state.
                </div>
              </div>
            ) : (
              upcomingDeadlines.map(
                ({
                  task,
                  isUrgent,
                  isWarn,
                  effectiveDashboardStatus,
                  actionLabel,
                  onAction,
                  isLoading,
                }) => (
                  <DeadlineItem
                    key={task.task_uuid ?? task.base_task_template.uuid}
                    deadlineDate={task.deadline_date}
                    title={task.task_title}
                    dashboardStatus={effectiveDashboardStatus}
                    isUrgent={isUrgent}
                    isWarn={isWarn}
                    actionLabel={actionLabel}
                    onAction={onAction}
                    isLoading={isLoading}
                  />
                )
              )
            )}
          </CollapsibleCard>

          <EntityDetailsCard
            stateDetail={stateDetail}
            entityAttributes={entityAttributes}
            entityName={entityName}
            entityPageUrl={
              isAdmin
                ? `/admin/crm/${groupId}/entity/${entityId}`
                : `/tax/entities/entity/${entityId}`
            }
            onAddMissingInfo={missingInfoModal.open}
          />

          <DocumentsCard
            entityAttributes={entityAttributes}
            stateDetail={stateDetail}
          />
        </div>

        <div className="t-flex t-flex-col t-gap-4 t-w-[280px] t-flex-shrink-0 sm:t-w-full">
          {showRenewWithInkleBanner && (
            <div className="t-border t-border-solid t-border-purple-20 t-rounded-lg t-bg-purple-0 t-p-4 t-flex t-flex-col t-gap-3">
              <div className="t-flex t-items-center t-gap-2">
                <span className="t-text-subtext t-font-semibold t-text-text-100">
                  Renew with Inkle
                </span>
              </div>
              <p className="t-m-0 t-text-body-sm t-text-text-60">
                {anniversaryDaysLeft === 0
                  ? "Your Registered Agent renewal may be coming up today."
                  : `Your Registered Agent renewal may be coming up in ${anniversaryDaysLeft} ${
                      anniversaryDaysLeft === 1 ? "day" : "days"
                    }.`}
              </p>
              <Button
                size="small"
                customType="primary"
                onClick={() => onRegisterNow("REGISTERED_AGENT")}
                isLoading={isRenewLoading}
                disabled={isRenewLoading}
              >
                Renew now
              </Button>
            </div>
          )}

          {showInkleRaBanner && (
            <div className="t-border t-border-solid t-border-purple-20 t-rounded-lg t-bg-purple-0 t-p-4 t-flex t-flex-col t-gap-3">
              <span className="t-text-purple">
                <Info size="20" />
              </span>
              <div className="t-flex t-items-center t-gap-2">
                <span className="t-text-subtext-sm t-text-text-100">
                  Move to Inkle RA
                </span>
              </div>
              <p className="t-m-0 t-text-body-sm-lite t-text-text-30">
                Get automatic deadline tracking and consolidated notices.
              </p>

              <Button
                size="xsmall"
                customType="link"
                onClick={() => onRegisterNow("REGISTERED_AGENT")}
                isLoading={isRenewLoading}
                disabled={isRenewLoading}
                rightIcon={<SmallRightArrow />}
              >
                Transfer now
              </Button>
            </div>
          )}

          {showInkleRaActiveBanner && (
            <div className="t-border t-border-solid t-border-purple-20 t-rounded-lg t-bg-purple-0 t-p-4 t-flex t-flex-col t-gap-3">
              <span className="t-text-purple">
                <Info size="20" />
              </span>
              <div className="t-flex t-items-center t-gap-2">
                <span className="t-text-subtext-sm t-text-text-100">
                  Inkle RA is active
                </span>
              </div>
              <div className="t-flex t-flex-col t-gap-1.5">
                <span className="t-flex t-items-start t-gap-1 t-text-body-sm-lite t-text-text-30">
                  <span className="t-mx-0.5 t-shrink-0">
                    <TickMarkBlue />
                  </span>{" "}
                  Deadlines tracked and surfaced automatically
                </span>
                <span className="t-flex t-items-start t-gap-1 t-text-body-sm-lite t-text-text-30">
                  <span className="t-mx-0.5 t-shrink-0">
                    <TickMarkBlue />
                  </span>{" "}
                  State notices forwarded directly to your Inkle dashboard
                </span>
              </div>
              {inkleRaExpirationDate && (
                <div className="t-flex t-items-center t-justify-between t-border-0 t-border-t t-border-solid t-border-purple-20 t-pt-3">
                  <span className="t-text-body-sm t-text-purple">
                    {`Renews ${dayjs(inkleRaExpirationDate).format(
                      "MMM D, YYYY"
                    )}`}
                  </span>
                  <Button
                    size="small"
                    customType="ghost"
                    onClick={() => onRegisterNow("REGISTERED_AGENT")}
                  >
                    View details
                  </Button>
                </div>
              )}
            </div>
          )}

          <StateCredentialsCard stateId={stateId} />
        </div>
      </div>

      {missingInfoModal.isOpen && missingFields.length > 0 && (
        <MissingInfoModal
          isOpen={missingInfoModal.isOpen}
          close={missingInfoModal.close}
          missingFields={missingFields}
          entityAttributes={entityAttributes}
          groupId={groupId}
          entityId={entityId}
          stateId={stateId}
          onLetInkleHandle={
            !stateDetail.is_home_state && !stateDetail.has_sos_intake
              ? () => onRegisterNow("SECRETARY_OF_STATE")
              : undefined
          }
        />
      )}

      {checkoutModal.isOpen && Boolean(invoice) && (
        <CheckoutModal
          entityId={entityId}
          type="invoices"
          onInvoicePaid={onPaymentSuccess}
          open={checkoutModal.isOpen}
          onClose={closeCheckoutModal}
          invoices={[invoice as BillingInvoice]}
          payerUserType={isForeignCA ? "FCA" : undefined}
        />
      )}
    </div>
  );
};
